import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {
    KEYWORD_OPERATOR_ROWS,
    KEYWORD_SEARCH_SAMPLES,
    KEYWORD_SEARCH_TOPICS,
} from "./keywordSearchSamples";

/**
 * Help dialog for recruiter-friendly keyword search with clickable samples.
 */
export const KeywordSearchHelpDialog = ({ open, onClose, onApplySample }) => {
    const handleApply = (query) => {
        onApplySample(query);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            aria-labelledby="keyword-search-help-title"
        >
            <DialogTitle id="keyword-search-help-title">
                Keyword search help
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            How it works
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            {KEYWORD_SEARCH_TOPICS}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            AND and OR are case-insensitive. Adjacent terms behave like
                            AND, and AND is evaluated before OR. Use parentheses when
                            combining them. Tokens shorter than ~3 characters may be
                            ignored.
                        </Typography>
                        <Table size="small" aria-label="Keyword search operators">
                            <TableHead>
                                <TableRow>
                                    <TableCell>What to type</TableCell>
                                    <TableCell>Meaning</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {KEYWORD_OPERATOR_ROWS.map((row) => (
                                    <TableRow key={row.syntax}>
                                        <TableCell
                                            sx={{
                                                fontFamily: "monospace",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {row.syntax}
                                        </TableCell>
                                        <TableCell>{row.meaning}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>

                    <Divider />

                    {KEYWORD_SEARCH_SAMPLES.map((section) => (
                        <Box key={section.domain}>
                            <Typography variant="subtitle1" gutterBottom>
                                {section.domain}
                            </Typography>
                            <Stack spacing={1}>
                                {section.samples.map((sample) => (
                                    <Button
                                        key={sample.query}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleApply(sample.query)}
                                        sx={{
                                            justifyContent: "flex-start",
                                            textTransform: "none",
                                            fontFamily: "monospace",
                                        }}
                                        aria-label={`Apply sample query ${sample.query}`}
                                    >
                                        <Stack
                                            alignItems="flex-start"
                                            spacing={0.25}
                                            sx={{ textAlign: "left", width: "100%" }}
                                        >
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                sx={{ fontFamily: "monospace" }}
                                            >
                                                {sample.query}
                                            </Typography>
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    fontFamily: "inherit",
                                                    textTransform: "none",
                                                }}
                                            >
                                                {sample.intent}
                                            </Typography>
                                        </Stack>
                                    </Button>
                                ))}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default KeywordSearchHelpDialog;
